import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { BulkInsertRequest } from 'models/request/BulkInsertRequest';
import { bulkInsert } from 'services/CeberusApiService';
import { LoadingFactory } from '../../../components/LoadingFactory'
import { OrganizationGrid } from './OrganizationGrid'
import 'antd/dist/antd.css';
import {
  Form,
  Button,
  Upload,
  Icon,
  message,
  Modal,
  Popconfirm,
  Card
} from 'antd';
import { PushNotification } from 'utils/PushNotification';

const OrganizationBulkInsertComponent = ({ form }) => {
  const [csv_file, setCsvFile] = useState<File | undefined>();
  const [csv_file_list, setCsvFileList] = useState([]);
  const [csv_organizations, setOrganizations] = useState([]);
  const [submited, setSubmited] = useState(false);
  const [is_loading, setIsLoading] = useState(false);

  const isNullOrEmpty = (value) => {
    if (value == undefined ||
      value == '' ||
      value == null) {
      return true;
    }

    return false;
  }

  const csv_error_message = 'It is only possible to import CSV files of up to 50 lines';
  const csv_error_process_message = 'Error processing CSV file!';
  const csv_field_error_message = 'Some fields are invalid, check values and try again';

  const validateFields = (organization) => {
    let returnStatus = true;

    if (isNullOrEmpty(organization.organizationId) ||
      isNullOrEmpty(organization.organizationName) ||
      isNullOrEmpty(organization.plan) ||
      isNullOrEmpty(organization.ownerIdentity) ||
      isNullOrEmpty(organization.isOrganizationTest) ||
      isNullOrEmpty(organization.hubspotCompanyId) ||
      isNullOrEmpty(organization.dealId)
    ) {
      returnStatus = false;
    }

    if (!isNullOrEmpty(organization.organizationId)) {
      const myRe = /^[a-z][a-z0-9-]{1,29}$/;
      if (!myRe.test(organization.organizationId)) {
        returnStatus = false;
      }
    }

    if (!isNullOrEmpty(organization.additionalAdminAccount)) {
      const myRe = /^([a-z]([a-z][a-z0-9.-]*@[a-z][a-z0-9.-]*[a-z0-9])?)(;[a-z]([a-z][a-z0-9.-]*@[a-z][a-z0-9.-]*[a-z0-9])?)*$/;
      if (!myRe.test(organization.additionalAdminAccount)) {
        returnStatus = false;
      }
      else {
        organization.additionalAdminAccount = organization.additionalAdminAccount.split(';');
      }
    }

    return returnStatus;
  }

  const processCSV = (str, delim = ',') => {
    str = str.replace('\r\n', '\n').replace('\r', '');
    const headers = str.slice(0, str.indexOf('\n')).split(delim);
    const rows = str.slice(str.indexOf('\n') + 1).split('\n');

    if (rows.length > 50) {
      message.error(csv_error_message);
      setOrganizations([]);
    }
    else {
      try {
        const newOrganizations = rows.map(row => {
          const values = row.split(delim);
          const eachObject = headers.reduce((obj, header, i) => {
            obj[header] = values[i] == null ? null : values[i].replace('\r', '');
            return obj;
          }, {})
          return eachObject;
        })
        setOrganizations(newOrganizations)
      }
      catch (err) {
        PushNotification.error(csv_error_process_message);
        setOrganizations([]);
      }
    }
  }

  const submit = async () => {
    setIsLoading(true);
    try {
      const invalidOrganizations = [];

      for (const organization of csv_organizations) {
        if (!validateFields(organization)) {
          organization.Status = csv_field_error_message;
          invalidOrganizations.push(organization);
        }
      }

      if (invalidOrganizations.length > 0) {
        resetFields();
        setOrganizations(invalidOrganizations);
        return;
      }

      const csvRecords = new BulkInsertRequest();
      csvRecords.organizationRecords = csv_organizations;
      const response = await bulkInsert(csvRecords);
      const organizationResponse = response.map((item) => {
        return {
          organizationId: item.OrganizationId,
          organizationName: item.OrganizationName,
          ownerIdentity: item.OwnerIdentity,
          additionalAdminAccount: item.AdditionalAdminAccount,
          isOrganizationTest: item.IsOrganizationTest,
          hubspotCompanyId: item.HubspotCompanyId,
          dealId: item.DealId,
          Status: item.Status,
          plan: item.Plan
        }
      });

      resetFields();
      setOrganizations(organizationResponse);
    }
    catch (err) {
      PushNotification.error(csv_error_process_message);
    }
  }

  const resetFields = () => {
    form.resetFields();
    setCsvFile(undefined);
    setCsvFileList([]);
    setSubmited(true);
    setIsLoading(false);
  };

  const pre_submit = () => {
    const file = csv_file;
    const reader = new FileReader();
    try {
      reader.onload = function (e) {
        const text = e.target.result;
        processCSV(text)
      }
      reader.readAsText(file);
    }
    catch (err) {
      PushNotification.error(csv_error_process_message);
    }
  }

  const clearAfterImport = () => {
    setSubmited(false);
    setOrganizations([]);
    setCsvFile(undefined);
  }

  const columns = [
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'Status',
    },
    {
      title: 'Organization Id',
      dataIndex: 'organization_id',
      key: 'organization_id',
    },
    {
      title: 'Organization Name',
      dataIndex: 'organization_name',
      key: 'organization_name',
    },
    {
      title: 'Owner Identity',
      dataIndex: 'owner_identity',
      key: 'owner_identity',
    },
    {
      title: 'Additional Admin',
      dataIndex: 'additional_admin',
      key: 'additional_admin',
    },
    {
      title: 'Organization test',
      dataIndex: 'is_organization_test',
      key: 'is_organization_test',
    },
    {
      title: 'Hubspot CompanyId',
      dataIndex: 'hubspot_companyId',
      key: 'hubspot_companyId',
    },
    {
      title: 'DealId',
      dataIndex: 'deal_id',
      key: 'deal_id',
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
    },
  ];

  useEffect(() => {
    if (csv_file)
      pre_submit();
  }, [csv_file])

  return (
    <>
      {is_loading && (<LoadingFactory />)}
      <Card>
        <form id='csv-form'>
          <h2>Bulk Insert</h2>
          <h3>{csv_error_message}</h3>
          <br />
          {csv_organizations.length == 0 || csv_organizations[0].Status == undefined ? <>
            <div style={{ display: 'inline-block' }}>
              <Upload
                multiple={false}
                accept='.csv'
                listType="text"
                beforeUpload={file => {
                  setCsvFile(file);
                  setCsvFileList([]);
                  const arr = new Array();
                  arr.push(file);
                  setCsvFileList(arr);
                  return false;
                }}
                onRemove={() => { setCsvFile(undefined); setCsvFileList([]) }}
                fileList={csv_file_list}
              >
                <Button><Icon type="upload" />Upload File</Button>
              </Upload>
            </div>
            <br />
          </> : null}
          <br />
          {csv_file !== undefined && csv_organizations.length > 0 && submited == false ?
            <>
              <OrganizationGrid columns={columns.filter(ea => ea.dataIndex !== 'Status')} csvArray={csv_organizations}></OrganizationGrid>
              {csv_organizations.length == 0 || csv_organizations[0].Status == undefined ? <>
                <Popconfirm title="This action is IRREVERSIBLE. Are you sure?" onConfirm={submit}>
                  <Button type="primary" disabled={is_loading}>Confirm and Import</Button>
                </Popconfirm>
              </> : null}
            </> : null}
        </form>
      </Card>
      <Modal
        centered
        title="Import Status"
        visible={submited}
        onOk={() => { clearAfterImport() }}
        onCancel={() => { clearAfterImport() }}
        width={1500}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <OrganizationGrid columns={columns} csvArray={csv_organizations}></OrganizationGrid>
      </Modal>
    </>
  );
}

export const OrganizationBulkInsert = Form.create({ name: 'organization-bulk-insert' })(
  OrganizationBulkInsertComponent
)

OrganizationBulkInsertComponent.propTypes = {
  form: PropTypes.any
}
